import { toast } from 'sonner'

import { projectsKeys, projectsService, queryClient, toApiError, type ProjectsService } from '@/api'
import type { AppStore } from '@/store/create-store'

import { workflowFields } from './mappers'
import { serverStatusOf, toServerStatus } from './server-status'
import type { ProjectRecord } from './types'

/** What the backend's update wants back: the project's own fields plus the new status, in the server's four words. */
function updateBody(project: ProjectRecord) {
  return { projectName: project.name, accountId: project.ownerAccountId ?? project.clientAccountId, assignedAccountId: project.clientAccountId, instructions: project.clientBrief, outputFormats: project.outputFormats ?? [], artworkSize: project.pageSize, status: toServerStatus(project.status), ...workflowFields(project) }
}

/**
 * Keeps the backend's project status in step with the work done here. When a step changes what a project's status means to the
 * backend (it went to review, it was approved, it was sent back to draft), the new status is sent, and the list reloads so what every screen shows is what the
 * backend now says. A status it refuses is reported once and not retried until the status changes again.
 */
export function startStatusSync(store: AppStore, service: ProjectsService = projectsService): () => void {
  const sending = new Set<string>()
  const refused = new Set<string>()

  async function send(project: ProjectRecord) {
    const key = `${project.id}:${toServerStatus(project.status)}`
    sending.add(key)
    try {
      await service.update(project.id, updateBody(project))
      await queryClient.invalidateQueries({ queryKey: projectsKeys.root })
    } catch (error) {
      refused.add(key)
      toast.error('Couldn’t update the project status on the server', { description: toApiError(error).message })
    } finally {
      sending.delete(key)
    }
  }

  const check = () => {
    for (const project of Object.values(store.getState().projects.projects.entities)) {
      // Only projects the server knows (it has told us a status), and only when what ours means to the server differs from what it holds.
      // "In progress" here is "draft" there, so moving between the two sends nothing.
      const held = project ? serverStatusOf(project.apiStatus) : undefined
      if (!project || !held || held === toServerStatus(project.status)) continue
      const key = `${project.id}:${toServerStatus(project.status)}`
      if (!sending.has(key) && !refused.has(key)) void send(project)
    }
  }
  return store.subscribe(check)
}
